// E-mail formatado (specs/25): o corpo do e-mail pode ser HTML simples, feito pelo editor do CRM.
// Este módulo é a fonte da verdade do que é aceito — usado na tela (editor/prévia) e no servidor (envio).
// Lista branca: só as marcações da barra de ferramentas; todo o resto é descartado (scripts, estilos,
// atributos de evento, iframes…). Texto antigo (puro) continua valendo.
import { renderizar, type ContextoRender, type ResultadoRender } from './render';

/** Paleta curta de cores do texto (o editor só oferece estas). */
export const CORES_TEXTO_EMAIL = [
  { nome: 'Preto', cor: '#1d1f23' },
  { nome: 'Azul', cor: '#2563eb' },
  { nome: 'Verde', cor: '#16a34a' },
  { nome: 'Vermelho', cor: '#dc2626' },
  { nome: 'Laranja', cor: '#ea580c' },
  { nome: 'Roxo', cor: '#7c3aed' },
] as const;
const CORES = new Set<string>(CORES_TEXTO_EMAIL.map((c) => c.cor));

/** Caminho público (assinado) das imagens do e-mail: /api/v1/publico/img/<token>. */
export const RE_IMAGEM_EMAIL = /^(?:https?:\/\/[^/\s"']+)?(\/api\/v1\/publico\/img\/[A-Za-z0-9_-]{20,80})$/;
export const IMAGENS_POR_EMAIL = 10;

/** O corpo é HTML do editor? (texto puro antigo não começa com uma marcação) */
export function ehHtmlEmail(s: string | null | undefined): boolean {
  return !!s && /^\s*<(p|h3|ul|ol|div|br|img|strong|em|b|i|u|span|a|font|h[1-6])[\s>/]/i.test(s);
}

export const escaparHtml = (s: string) => s.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

/** Texto puro → HTML do editor (parágrafos e quebras de linha). */
export function textoParaHtml(texto: string): string {
  const blocos = texto.replace(/\r\n/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return blocos.length ? blocos.map((b) => `<p>${escaparHtml(b).replace(/\n/g, '<br>')}</p>`).join('') : '';
}

function corHex(v: string): string | null {
  const s = v.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  if (/^#[0-9a-f]{3}$/.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  const m = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/.exec(s);
  if (m) return `#${[m[1], m[2], m[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('')}`;
  return null;
}

/** Valor de um atributo dentro do trecho de atributos de uma marcação. */
function attr(atributos: string, nome: string): string | null {
  const m = new RegExp(`(?:^|\\s)${nome}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i').exec(atributos);
  if (!m) return null;
  return (m[1] ?? m[2] ?? m[3] ?? '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

const BLOCO: Record<string, string> = { p: 'p', div: 'p', h1: 'h3', h2: 'h3', h3: 'h3', h4: 'h3', h5: 'h3', h6: 'h3', ul: 'ul', ol: 'ol', li: 'li', blockquote: 'p' };
const INLINE: Record<string, string> = { strong: 'strong', b: 'strong', em: 'em', i: 'em', u: 'u' };

/**
 * Limpa o HTML pela lista branca. Saída só com: p, br, h3, ul, ol, li, strong, em, u,
 * a[href http(s)/mailto], span[style=color da paleta], img[src = imagem do CRM, alt, width].
 */
export function sanitizarHtmlEmail(html: string): string {
  const fonte = html
    .replace(/<!--[\s\S]*?-->/g, '')
    // conteúdo dessas marcações nunca é texto do e-mail
    .replace(/<(script|style|head|title|template|iframe|object|svg|math)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<(script|style|head|title|template|iframe|object|svg|math)\b[^>]*\/?>/gi, '');
  const pilha: string[] = [];
  let saida = '';
  for (const parte of fonte.split(/(<[^<>]*>)/)) {
    if (!parte) continue;
    if (!/^<[^<>]*>$/.test(parte)) {
      saida += parte.replace(/</g, '&#60;').replace(/>/g, '&#62;');
      continue;
    }
    const m = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)([^>]*?)\/?\s*>$/.exec(parte);
    if (!m) {
      saida += escaparHtml(parte);
      continue;
    }
    const fecha = m[1] === '/';
    const nome = m[2]!.toLowerCase();
    const atributos = m[3] ?? '';
    if (nome === 'br') { if (!fecha) saida += '<br>'; continue; }
    if (nome === 'img') {
      if (fecha) continue;
      const src = RE_IMAGEM_EMAIL.exec(attr(atributos, 'src') ?? '')?.[1];
      if (!src) continue;
      const alt = attr(atributos, 'alt');
      const largura = Number(attr(atributos, 'width'));
      saida += `<img src="${src}"${alt ? ` alt="${escaparHtml(alt.slice(0, 120))}"` : ''}${largura >= 16 && largura <= 640 ? ` width="${Math.round(largura)}"` : ''}>`;
      continue;
    }
    let alvo = BLOCO[nome] ?? INLINE[nome] ?? (nome === 'a' ? 'a' : nome === 'span' || nome === 'font' ? 'span' : null);
    if (!alvo) continue; // marcação desconhecida: some, o texto de dentro fica
    if (fecha) {
      const i = pilha.lastIndexOf(alvo);
      if (i < 0) continue;
      // fecha o que ficou aberto por dentro (HTML colado mal formado)
      for (let j = pilha.length - 1; j >= i; j--) saida += `</${pilha[j]}>`;
      pilha.length = i;
      continue;
    }
    let abre = `<${alvo}>`;
    if (alvo === 'a') {
      const href = (attr(atributos, 'href') ?? '').trim();
      if (!/^(https?:\/\/|mailto:)[^\s<>"']+$/i.test(href)) { alvo = 'span'; abre = '<span>'; }
      else abre = `<a href="${escaparHtml(href)}">`;
    } else if (alvo === 'span') {
      const estilo = attr(atributos, 'style') ?? '';
      const cor = corHex(attr(atributos, 'color') ?? /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(estilo)?.[1] ?? '');
      abre = cor && CORES.has(cor) ? `<span style="color:${cor}">` : '<span>';
    }
    // parágrafo/título dentro de parágrafo/título (ex.: <div><p> colado do Gmail): fecha o de fora
    if ((alvo === 'p' || alvo === 'h3') && pilha.some((x) => x === 'p' || x === 'h3')) {
      while (pilha.length) {
        const x = pilha.pop()!;
        saida += `</${x}>`;
        if (x === 'p' || x === 'h3') break;
      }
    }
    pilha.push(alvo);
    saida += abre;
  }
  for (let j = pilha.length - 1; j >= 0; j--) saida += `</${pilha[j]}>`;
  return saida
    .replace(/<span>([\s\S]*?)<\/span>/g, '$1')
    .replace(/<(p|h3|li)>\s*<\/\1>/g, '')
    .replace(/(<br>\s*){3,}/g, '<br><br>')
    .trim();
}

const ENTIDADES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
const decodificar = (s: string) =>
  s.replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (t, e: string) =>
    e[0] === '#' ? String.fromCodePoint(e[1]?.toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : Number(e.slice(1))) : ENTIDADES[e.toLowerCase()] ?? t);

/** HTML do editor → texto puro (parte texto do e-mail, histórico e trechos). */
export function htmlParaTexto(html: string): string {
  return decodificar(
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<li[^>]*>/gi, '\n• ')
      .replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href: string, txt: string) => {
        const t = txt.replace(/<[^>]*>/g, '').trim();
        return t && t !== href ? `${t} (${href})` : href;
      })
      .replace(/<img[^>]*>/gi, '')
      .replace(/<\/(p|h3|ul|ol)>/gi, '\n\n')
      .replace(/<[^>]*>/g, ''),
  )
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Variáveis num corpo HTML: os valores do cliente entram escapados. */
export function renderizarEmail(corpo: string, ctx: ContextoRender): ResultadoRender & { html: boolean } {
  if (!ehHtmlEmail(corpo)) return { ...renderizar(corpo, ctx), html: false };
  const seguro: ContextoRender = {};
  for (const [k, v] of Object.entries(ctx)) seguro[k as keyof ContextoRender] = v == null ? v : escaparHtml(String(v));
  return { ...renderizar(corpo, seguro), html: true };
}

/** Estilos em linha para os programas de e-mail (Gmail/Outlook ignoram CSS de fora). */
export function estilizarHtmlEmail(html: string): string {
  return html
    .replace(/<p>/g, '<p style="margin:0 0 14px">')
    .replace(/<h3>/g, '<h3 style="margin:0 0 12px;font-size:18px;line-height:1.3;color:#1d1f23">')
    .replace(/<(ul|ol)>/g, '<$1 style="margin:0 0 14px;padding-left:22px">')
    .replace(/<li>/g, '<li style="margin:0 0 4px">')
    .replace(/<a href=/g, '<a style="color:#2563eb" href=')
    .replace(/<img /g, '<img style="max-width:100%;height:auto;display:block;margin:6px 0 14px;border:0" ');
}

/** Caminhos de imagem do CRM usados no corpo (para embutir no envio). */
export function imagensDoHtml(html: string): string[] {
  return [...new Set([...html.matchAll(/<img src="(\/api\/v1\/publico\/img\/[A-Za-z0-9_-]+)"/g)].map((m) => m[1]!))];
}
